import React, {useEffect, useRef} from 'react';
import styles from './styles.scss';
import {useParams} from 'react-router-dom';
import ReactInfiniteScroller from 'react-infinite-scroller';
import Loading from '../../Loading';
import ErrorState from '../../ErrorState';
import ActionBar, { FilterOperatorInputPercent } from '../../ActionBar';
import {SensorListView} from '../../InConfiguration';
import COLUMN_SETS from '../../InConfiguration/columnSets';

import {ON_PREM} from '../../../utils/environment';


import {AppBar, AppBarSection, AppBarTitle, Button } from '@densityco/ui';
import HorizontalForm from '../../HorizontalForm';
import Sidebar from '../../Sidebar';

import { ResourceStatus } from '../../../types/resource';
import { FilterOperator } from '../../../types/filters';
import { deserializeOrganization } from '../../../types/all-organizations';
import {useRequiredSessionToken} from '../../../utils/session-token';

import {MultiPointInstallationAddSensorSidebarActionTypes} from '../../../types/multi-point-installation-add-sensor-sidebar';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import MultiPointInstallationAddSensorSidebarStore
  from '../../../rx-stores/multi-point-installation-add-sensor-sidebar';

const AddSensorSidebar: React.FunctionComponent<{
  open: boolean;
  onSubmit: (
    sensors: Array<{ serialNumber: string; doorwayName: string }>,
  ) => void;
  onDismiss: () => void;
}> = ({ open, onDismiss, onSubmit }) => {
  const token = useRequiredSessionToken();
  const { organizationIdOrAll } = useParams();
  let scrollParentRef = useRef<HTMLDivElement>(null);

  const state = useRxStore(MultiPointInstallationAddSensorSidebarStore);
  const dispatch = useRxDispatch();

  useEffect(() => {
    dispatch({
      type:
        MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_OPEN,
      organizationId: deserializeOrganization(organizationIdOrAll),
    });
  }, [open]);

  let content;

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      content = <Loading />;
      break;

    case ResourceStatus.ERROR:
      content = <ErrorState error={state.resource.error} />;
      break;

    case ResourceStatus.COMPLETE:
      const columnSet = COLUMN_SETS.sidebar({
        onPrem: ON_PREM
      });
      content = (
        <div className={styles.scroll} ref={scrollParentRef}>
          <ReactInfiniteScroller
            pageStart={1}
            loadMore={page => {
              dispatch({
                type:
                  MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_PAGE,
                value: page,
              });
            }}
            hasMore={
              state.resource.data.total >
                state.resource.data.sensors.length &&
              state.resource.data.fetchingMoreSensors.status ===
                ResourceStatus.IDLE
            }
            useWindow={false}
            getScrollParent={() => scrollParentRef.current}
          >
            <SensorListView
              data={state.resource.data.sensors}
              columnSet={columnSet}
              sort={state.sort}
              onChangeSort={(column, direction) =>
                dispatch({
                  type:
                    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SORT,
                  column,
                  direction,
                })
              }
              showDetails={false}
              showChecks
              selectedSensorSerialNumbers={state.selectedSensorSerialNumbers}
              onChangeChecks={(serialNumbers, checked) => {
                dispatch({
                  type:
                    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_CHANGE_CHECKS,
                  serialNumbers,
                  checked,
                });
              }}
            />
          </ReactInfiniteScroller>
          {state.resource.data.fetchingMoreSensors.status ===
          ResourceStatus.LOADING ? (
            <div className={styles.infiniteScrollLoading}>
              Loading more sensors...
            </div>
          ) : null}
        </div>
      );
      break;
  }

  return (
    <Sidebar
      visible={open}
      onBlur={() => onDismiss()}
      onEscape={() => onDismiss()}
      width={800}
    >
      <div className={styles.wrapper} data-test-id="mpi-add-sensor-sidebar">
        <AppBar>
          <AppBarTitle>Add Sensor</AppBarTitle>
          <AppBarSection>
            <HorizontalForm>
              <Button
                variant="underline"
                onClick={() => {
                  onDismiss();
                }}
                data-test-id="mpi-add-sensor-sidebar-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                disabled={state.selectedSensorSerialNumbers.length === 0}
                onClick={() => {
                  onSubmit(
                    state.selectedSensorSerialNumbers.map(serialNumber => {
                      if (state.resource.status !== ResourceStatus.COMPLETE) {
                        return;
                      }
                      const item = state.resource.data.sensors.find(
                        i => i.serial_number === serialNumber,
                      );
                      return {
                        serialNumber: item.serial_number,
                        doorwayName: item.doorway_name,
                      };
                    }),
                  );
                }}
                data-test-id="mpi-add-sensor-sidebar-submit"
              >
                Add to MPI
              </Button>
            </HorizontalForm>
          </AppBarSection>
        </AppBar>

        <ActionBar
          units="Sensors"
          totalItemCount={
            state.resource.status === ResourceStatus.COMPLETE
              ? state.resource.data.total
              : null
          }
          selectedItemCount={state.selectedSensorSerialNumbers.length}
          onClearSelectedItems={() => {
            if (state.resource.status !== ResourceStatus.COMPLETE) {
              return;
            }
            dispatch({
              type:
                MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_CHANGE_CHECKS,
              serialNumbers: state.resource.data.sensors.map(i => i.serial_number),
              checked: false,
            });
          }}
          showSearch
          searchPlaceholder="Search serial, doorway, or space"
          searchWidth={300}
          searchValue={state.searchText}
          onChangeSearch={e => {
            dispatch({
              type:
                MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SEARCH_TEXT,
              text: e.target.value,
            });
          }}
          filterDefinitions={[
            {
              id: 'total_accuracy',
              label: 'Total Accuracy',
              operators: [
                {
                  id: 'lt',
                  label: 'Less Than',
                  allowMultiple: false,
                  isValueValid: s => !isNaN(parseFloat(s)),
                  initialValue: '',
                  component: FilterOperatorInputPercent,
                } as FilterOperator<string>,
                {
                  id: 'gt',
                  label: 'Greater Than',
                  allowMultiple: false,
                  initialValue: '',
                  isValueValid: s => !isNaN(parseFloat(s)),
                  component: FilterOperatorInputPercent,
                } as FilterOperator<string>,
              ],
            },
          ]}
          filters={state.filters}
          onChangeFilters={filters => {
            dispatch({
              type:
                MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_FILTERS,
              filters,
            });
          }}
        />

        {content}
      </div>
    </Sidebar>
  );
};

export default AddSensorSidebar;
